import React, { useCallback, useEffect, useRef, useState } from 'react';
import { Modal } from 'antd';
import { message } from '../utils/antdMessage';
import apiFetch from '../utils/apiFetch';
import { formatBytes, postObjectToOss } from '../utils/orthoToolbox';

/**
 * 地图图层任务：列表/发布层/上传切片 CRUD 与管理弹窗轮询
 */
export default function useMapLayerTasks({
  canMapLayerManage = false,
  canMapLayerView = false,
  holdPanelOpenRef,
  setExpanded,
  setPinned,
  leaveTimerRef
}) {
  const [mapLayerTasks, setMapLayerTasks] = useState([]);
  const [mapLayerKind, setMapLayerKind] = useState('road');
  const [mapLayerTitle, setMapLayerTitle] = useState('');
  const [mapLayerDescription, setMapLayerDescription] = useState('');
  const [contourInterval, setContourInterval] = useState(10);
  const [mapLayerBusy, setMapLayerBusy] = useState(false);
  const [mapPublishedLayers, setMapPublishedLayers] = useState([]);
  const [sliceReadyHints, setSliceReadyHints] = useState([]);
  /** 面板内上传进度（避免仅靠顶部 toast，选文件时面板易收起） */
  const [mapLayerUpload, setMapLayerUpload] = useState(null);
  // { taskId, fileName, fileSize, phase, percent, error, done }
  /** 管理弹窗：仅「地图图层管理」权限 */
  const [mapManageOpen, setMapManageOpen] = useState(false);
  const [mapManageKind, setMapManageKind] = useState('road');
  const mapLayerFileRef = useRef(null);
  /** 选文件期间保存任务 ID（勿用 input.dataset，重渲染会丢） */
  const pendingMapLayerTaskIdRef = useRef(null);
  const mapLayerTasksRef = useRef([]);

  useEffect(() => {
    mapLayerTasksRef.current = mapLayerTasks;
  }, [mapLayerTasks]);

  const readJson = async (res) => {
    const data = await res.json().catch(() => ({}));
    if (!res.ok || data.success === false) {
      const err = new Error(data.message || data.error || `请求失败 ${res.status}`);
      err.code = data.error;
      err.payload = data;
      throw err;
    }
    return data;
  };

  const loadMapLayerTasks = useCallback(async () => {
    if (!canMapLayerManage) return;
    try {
      const data = await readJson(await apiFetch('/api/map-layers/tasks'));
      // pg BIGINT 偶发字符串，统一成数字避免 === 匹配失败
      setMapLayerTasks((data.tasks || []).map((t) => ({ ...t, id: Number(t.id) })));
    } catch (e) {
      console.warn('[map-layers] 任务列表', e);
    }
  }, [canMapLayerManage]);

  const loadMapPublishedLayers = useCallback(async () => {
    if (!canMapLayerView && !canMapLayerManage) return;
    try {
      const data = await readJson(await apiFetch('/api/map-layers/layers'));
      setMapPublishedLayers((data.layers || []).map((ly) => ({
        ...ly,
        id: Number(ly.id)
      })));
    } catch (e) {
      console.warn('[map-layers] 发布图层', e);
    }
  }, [canMapLayerView, canMapLayerManage]);

  const loadSliceReadyCheck = useCallback(async () => {
    if (!canMapLayerManage) return;
    try {
      const data = await readJson(await apiFetch('/api/map-layers/ready-check'));
      setSliceReadyHints(data.hints || []);
    } catch (_) {
      setSliceReadyHints([]);
    }
  }, [canMapLayerManage]);

  /** 管理弹窗打开时加快刷新，右侧状态/进度更及时 */
  useEffect(() => {
    if (!mapManageOpen || !canMapLayerManage) return undefined;
    loadMapLayerTasks();
    const t = setInterval(loadMapLayerTasks, 5000);
    return () => clearInterval(t);
  }, [mapManageOpen, canMapLayerManage, loadMapLayerTasks]);

  const createMapLayerTask = async () => {
    const kind = mapManageOpen ? mapManageKind : mapLayerKind;
    const title = mapLayerTitle.trim();
    if (!title) {
      message.warning('请填写任务名称（将显示在左侧竖型菜单）');
      return;
    }
    setMapLayerBusy(true);
    try {
      const body = {
        kind,
        title,
        description: mapLayerDescription.trim() || undefined
      };
      if (kind === 'contour') body.contourInterval = contourInterval;
      const data = await readJson(await apiFetch('/api/map-layers/tasks', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(body)
      }));
      message.success(data.hint || `已创建${kind === 'road' ? '路网' : '等高线'}任务 #${data.task?.id}`);
      setMapLayerTitle('');
      setMapLayerDescription('');
      setMapLayerKind(kind);
      await loadMapLayerTasks();
    } catch (e) {
      message.error(e.message || '创建失败');
    } finally {
      setMapLayerBusy(false);
    }
  };

  const uploadMapLayerSource = async (task, file) => {
    const taskId = Number(task?.id);
    if (!Number.isFinite(taskId) || taskId <= 0 || !file) return;
    holdPanelOpenRef.current = true;
    setExpanded(true);
    setPinned(true);
    setMapLayerBusy(true);
    setMapLayerUpload({
      taskId,
      fileName: file.name,
      fileSize: file.size,
      phase: 'credentials',
      percent: 0,
      error: '',
      done: false
    });
    try {
      const credData = await readJson(await apiFetch('/api/map-layers/upload/credentials', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ taskId, fileName: file.name })
      }));
      const upload = credData.upload;
      if (upload?.mode === 'disabled') {
        throw new Error(upload.message || 'OSS 未配置，无法直传');
      }
      const objectKey = upload?.objectKey || upload?.key;
      if (!objectKey) throw new Error('未返回 objectKey');
      if (credData.maxSizeBytes && file.size > credData.maxSizeBytes) {
        throw new Error(`文件超过上限（${formatBytes(credData.maxSizeBytes)}）`);
      }

      setMapLayerUpload((prev) => ({
        ...prev,
        phase: 'uploading',
        percent: 0
      }));
      await postObjectToOss(upload, file, objectKey, ({ percent }) => {
        setMapLayerUpload((prev) => (prev ? { ...prev, phase: 'uploading', percent } : prev));
      });

      setMapLayerUpload((prev) => ({
        ...prev,
        phase: 'registering',
        percent: 100
      }));
      await readJson(await apiFetch('/api/map-layers/upload/complete', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          taskId,
          ossKey: objectKey,
          bytes: file.size
        })
      }));
      setMapLayerUpload({
        taskId,
        fileName: file.name,
        fileSize: file.size,
        phase: 'done',
        percent: 100,
        error: '',
        done: true
      });
      message.success(`#${taskId} 已上传 ${formatBytes(file.size)}，进入排队切片`);
      await loadMapLayerTasks();
    } catch (e) {
      const errText = e.message || '上传失败';
      setMapLayerUpload((prev) => ({
        ...(prev || { taskId, fileName: file.name, fileSize: file.size, percent: 0 }),
        phase: 'error',
        error: errText,
        done: false
      }));
      message.error(errText);
    } finally {
      setMapLayerBusy(false);
      holdPanelOpenRef.current = false;
      if (mapLayerFileRef.current) mapLayerFileRef.current.value = '';
    }
  };

  const cancelMapLayerTask = async (id) => {
    setMapLayerBusy(true);
    try {
      const data = await readJson(await apiFetch(`/api/map-layers/tasks/${id}/cancel`, { method: 'POST' }));
      const n = data.ossPurge?.deleted;
      message.success(
        typeof n === 'number'
          ? `已取消，并清理 OSS ${n} 个残留对象`
          : (data.message || '已取消')
      );
      setMapLayerUpload((prev) => (prev?.taskId === id ? null : prev));
      await loadMapLayerTasks();
    } catch (e) {
      message.error(e.message || '取消失败');
    } finally {
      setMapLayerBusy(false);
    }
  };

  const deleteMapLayerTask = async (id) => {
    setMapLayerBusy(true);
    try {
      const data = await readJson(await apiFetch(`/api/map-layers/tasks/${id}`, { method: 'DELETE' }));
      const n = data.ossPurge?.deleted;
      message.success(
        typeof n === 'number'
          ? `已删除，并清理 OSS ${n} 个残留对象`
          : (data.message || '已删除')
      );
      setMapLayerUpload((prev) => (prev?.taskId === id ? null : prev));
      await loadMapLayerTasks();
    } catch (e) {
      message.error(e.message || '删除失败');
    } finally {
      setMapLayerBusy(false);
    }
  };

  const startMapLayerSlice = async (id) => {
    setMapLayerBusy(true);
    try {
      const data = await readJson(await apiFetch(`/api/map-layers/tasks/${id}/start-slice`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({})
      }));
      if (data.manual && data.bootToken) {
        Modal.info({
          title: '本地/虚拟机切片 · bootToken',
          width: 560,
          content: (
            <div>
              <div style={{ marginBottom: 8 }}>{data.message}</div>
              <div><b>taskId：</b>{id}</div>
              <div><b>callbackBase：</b>{data.callbackBase || '-'}</div>
              <div style={{ marginTop: 8 }}><b>bootToken：</b></div>
              <pre style={{ whiteSpace: 'pre-wrap', wordBreak: 'break-all', background: '#f8fafc', padding: 8 }}>
                {data.bootToken}
              </pre>
            </div>
          )
        });
      } else {
        message.success(data.message || (data.dryRun ? 'DRY_RUN 切片完成' : '已启动切片'));
      }
      await loadMapLayerTasks();
      await loadMapPublishedLayers();
    } catch (e) {
      message.error(e.message || '启动切片失败');
    } finally {
      setMapLayerBusy(false);
    }
  };

  const requeueMapLayerTask = async (id) => {
    setMapLayerBusy(true);
    try {
      const data = await readJson(await apiFetch(`/api/map-layers/tasks/${id}/requeue`, { method: 'POST' }));
      message.success(data.message || '已重新排队，可再次启动切片');
      await loadMapLayerTasks();
      await loadMapPublishedLayers();
    } catch (e) {
      message.error(e.message || '重新排队失败');
    } finally {
      setMapLayerBusy(false);
    }
  };

  const openMapManage = (kind, { createFocus = false } = {}) => {
    if (!canMapLayerManage) {
      message.warning('需要「地图图层管理」权限');
      return;
    }
    setMapManageKind(kind);
    setMapLayerKind(kind);
    setMapManageOpen(true);
    setPinned(true);
    setExpanded(true);
    loadMapLayerTasks();
    if (createFocus) {
      setMapLayerTitle('');
    }
  };

  const pickMapLayerFile = (taskId) => {
    if (!mapLayerFileRef.current) return;
    const id = Number(taskId);
    pendingMapLayerTaskIdRef.current = id;
    holdPanelOpenRef.current = true;
    setExpanded(true);
    setPinned(true);
    if (leaveTimerRef.current) {
      clearTimeout(leaveTimerRef.current);
      leaveTimerRef.current = null;
    }
    setMapLayerUpload({
      taskId: id,
      fileName: '',
      fileSize: 0,
      phase: 'picking',
      percent: 0,
      error: '',
      done: false
    });
    // 延迟到下一帧再打开文件框，避免 setState 重渲染冲掉本次 click
    window.requestAnimationFrame(() => {
      mapLayerFileRef.current?.click();
    });
  };

  return {
    mapLayerTasks,
    setMapLayerTasks,
    mapLayerKind,
    setMapLayerKind,
    mapLayerTitle,
    setMapLayerTitle,
    mapLayerDescription,
    setMapLayerDescription,
    contourInterval,
    setContourInterval,
    mapLayerBusy,
    setMapLayerBusy,
    mapPublishedLayers,
    setMapPublishedLayers,
    sliceReadyHints,
    setSliceReadyHints,
    mapLayerUpload,
    setMapLayerUpload,
    mapManageOpen,
    setMapManageOpen,
    mapManageKind,
    setMapManageKind,
    mapLayerFileRef,
    pendingMapLayerTaskIdRef,
    mapLayerTasksRef,
    loadMapLayerTasks,
    loadMapPublishedLayers,
    loadSliceReadyCheck,
    createMapLayerTask,
    uploadMapLayerSource,
    cancelMapLayerTask,
    deleteMapLayerTask,
    startMapLayerSlice,
    requeueMapLayerTask,
    openMapManage,
    pickMapLayerFile
  };
}
